import type { PostHog } from 'posthog-js';

// ─────────────────────────────────────────────────────────────────────────────
// First-touch attribution capture.
//
// Snapshots WHERE a visitor originally came from (organic search / social / paid /
// referral / direct) into a cross-subdomain cookie on `.agora.red`, then registers
// it as PostHog super-properties so EVERY event carries the true origin.
//
// Why this exists: `auth_signup_completed` fires on `/callback`, AFTER the Google
// OAuth full-page roundtrip (`accounts.google.com` → `/callback`) has overwritten
// PostHog's native session referrer with `accounts.google.com`. Person-level
// `$initial_*` props are empty (person_profiles: identified_only) or contaminated,
// so the real origin was previously unrecoverable at signup time. The cookie is
// written on the FIRST landing (here in Flash, or upstream on the agora.red landing
// by Nightwing) and scoped to `.agora.red`, so it survives BOTH the subdomain hop
// and the OAuth roundtrip.
//
// Purely additive: never touches `authorize()` / `parseHash()` / the callback.
//
// Kill-switch: `VITE_FIRST_TOUCH_ATTRIBUTION=0` disables (default ON).
// ─────────────────────────────────────────────────────────────────────────────

const ENABLED = import.meta.env.VITE_FIRST_TOUCH_ATTRIBUTION !== '0';
const COOKIE = 'agora_ft';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30d — matches Nightwing's first-touch TTL.

// Search / social host fragments (substring match on the referrer hostname).
const SEARCH_HOSTS = [
  'google.',
  'bing.',
  'duckduckgo.',
  'search.brave.',
  'yahoo.',
  'ecosia.',
  'googlequicksearchbox'
];
const SOCIAL_HOSTS = [
  'instagram.',
  'facebook.',
  'lm.facebook.',
  't.co',
  'tiktok.',
  'linkedin.',
  'youtube.',
  'wa.me',
  'whatsapp'
];
const PAID_MEDIUMS = ['paid', 'paid_social', 'cpc', 'cpm', 'ppc'];
const PAID_CLICK_IDS = ['fbclid', 'gclid', 'ttclid', 'msclkid'];
const ATTRIBUTION_VERSION = '2';
const MAX_ATTRIBUTION_VALUE_LENGTH = 256;

// Referrers that are internal hops or the OAuth roundtrip — NOT a real first touch.
// Capturing these would re-introduce the exact `accounts.google.com` contamination
// this fix removes, so we skip writing the cookie when the referrer is one of them.
const INTERNAL_REFERRER_HOSTS = ['agora.red', 'accounts.google.com'];

interface FirstTouch {
  ch: string; // derived channel
  r?: string; // referrer host
  s?: string; // utm_source
  m?: string; // utm_medium
  ca?: string; // utm_campaign
  te?: string; // utm_term
  co?: string; // utm_content
  cid?: string; // stable campaign id
  asid?: string; // stable ad set id
  aid?: string; // stable ad id
  crid?: string; // stable creative id
  pl?: string; // placement
  p?: string; // landing path
  t: string; // ISO timestamp
  tp?: string; // Agora-owned touchpoint id
  v?: string; // attribution contract version
}

const clean = (value: string | null | undefined): string | undefined => {
  const normalized = value?.trim();
  return normalized ? normalized.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH) : undefined;
};

const firstParam = (params: URLSearchParams, names: string[]): string | undefined => {
  for (const name of names) {
    const value = clean(params.get(name));
    if (value) return value;
  }
  return undefined;
};

const legacyNumericId = (value: string | undefined): string | undefined =>
  value && /^\d{8,}$/.test(value) ? value : undefined;

const createTouchpointId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `tp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
  }
};

const classifyChannel = (host: string, utmMedium: string | null, hasPaidClickId: boolean): string => {
  const medium = (utmMedium || '').toLowerCase();
  if (hasPaidClickId || PAID_MEDIUMS.includes(medium)) return 'paid';
  if (!host) return 'direct';
  if (SEARCH_HOSTS.some((h) => host.includes(h))) return 'organic_search';
  if (SOCIAL_HOSTS.some((h) => host.includes(h))) return 'organic_social';
  return 'referral';
};

const isInternalHost = (host: string): boolean =>
  INTERNAL_REFERRER_HOSTS.some((d) => host === d || host.endsWith('.' + d));

const readCookie = (): FirstTouch | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1])) as FirstTouch;
  } catch {
    return null;
  }
};

const writeCookie = (ft: FirstTouch): void => {
  const host = window.location.hostname;
  // Apex-scope on prod so the cookie survives the cross-subdomain hop + OAuth
  // roundtrip. Skip the Domain attribute in dev (localhost rejects it).
  const domainAttr = host.endsWith('agora.red') ? '; domain=.agora.red' : '';
  document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(ft))}; path=/; max-age=${COOKIE_MAX_AGE}${domainAttr}; samesite=lax`;
};

const readCurrentParams = (): URLSearchParams => {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams();
  }
};

const getOrCreateFirstTouch = (): FirstTouch | null => {
  if (!ENABLED) return null;
  let ft = readCookie();

  // Cookies written by the v1 contract remain valid. Enrich them in place with a
  // stable Agora touchpoint so signup and every later product event can be joined.
  if (ft) {
    if (!ft.tp || ft.v !== ATTRIBUTION_VERSION) {
      ft = { ...ft, tp: ft.tp || createTouchpointId(), v: ATTRIBUTION_VERSION };
      writeCookie(ft);
    }
    return ft;
  }

  const referrer = document.referrer || '';
  let host: string;
  try {
    host = referrer ? new URL(referrer).hostname : '';
  } catch {
    host = '';
  }
  if (isInternalHost(host)) return null;

  const params = readCurrentParams();
  const utmSource = clean(params.get('utm_source'));
  const utmMedium = clean(params.get('utm_medium'));
  const utmCampaign = clean(params.get('utm_campaign'));
  const utmTerm = clean(params.get('utm_term'));
  const utmContent = clean(params.get('utm_content'));
  const campaignId = firstParam(params, ['campaign_id', 'utm_campaign_id']) || legacyNumericId(utmCampaign);
  const adsetId = firstParam(params, ['adset_id', 'utm_adset_id']) || legacyNumericId(utmTerm);
  const adId = firstParam(params, ['ad_id', 'utm_ad_id', 'utm_adid']) || legacyNumericId(utmContent);
  const creativeId = firstParam(params, ['creative_id', 'utm_creative_id']);
  const placement =
    firstParam(params, ['placement']) ||
    (utmMedium && /(feed|reels|stories)/i.test(utmMedium) ? utmMedium : undefined);
  const hasPaidClickId = PAID_CLICK_IDS.some((key) => clean(params.get(key)));

  ft = {
    ch: classifyChannel(host, utmMedium ?? null, hasPaidClickId),
    ...(host && { r: host }),
    ...(utmSource && { s: utmSource }),
    ...(utmMedium && { m: utmMedium }),
    ...(utmCampaign && { ca: utmCampaign }),
    ...(utmTerm && { te: utmTerm }),
    ...(utmContent && { co: utmContent }),
    ...(campaignId && { cid: campaignId }),
    ...(adsetId && { asid: adsetId }),
    ...(adId && { aid: adId }),
    ...(creativeId && { crid: creativeId }),
    ...(placement && { pl: placement }),
    p: window.location.pathname,
    t: new Date().toISOString(),
    tp: createTouchpointId(),
    v: ATTRIBUTION_VERSION
  };
  writeCookie(ft);
  return ft;
};

/**
 * Canonical, privacy-reviewed acquisition payload persisted by Cyclone on signup.
 * Raw click ids are intentionally excluded: Meta matching uses fbc/fbp through the
 * reviewed CAPI user_data contract, while campaign correlation uses stable ids.
 */
export const getSignupAttribution = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};

  try {
    const params = readCurrentParams();
    const current: Record<string, string> = {};
    for (const [key, raw] of params.entries()) {
      if (!key.startsWith('utm_')) continue;
      const value = clean(raw);
      if (value) current[key.slice(0, 80)] = value;
    }
    const landingVisitId = window.__agoraLandingProbe?.visitId;
    if (landingVisitId && /^[a-zA-Z0-9:_-]{1,64}$/.test(landingVisitId)) {
      current.landing_visit_id = landingVisitId;
    }

    const canonical = {
      campaign_id:
        firstParam(params, ['campaign_id', 'utm_campaign_id']) || legacyNumericId(current.utm_campaign),
      adset_id: firstParam(params, ['adset_id', 'utm_adset_id']) || legacyNumericId(current.utm_term),
      ad_id: firstParam(params, ['ad_id', 'utm_ad_id', 'utm_adid']) || legacyNumericId(current.utm_content),
      creative_id: firstParam(params, ['creative_id', 'utm_creative_id']),
      placement: firstParam(params, ['placement']),
      // La página de agora.red donde se abrió el registro; nightwing la manda en el redirect de Google.
      signup_page: firstParam(params, ['signup_page'])
    };
    for (const [key, value] of Object.entries(canonical)) {
      if (value) current[key] = value;
    }

    const ft = getOrCreateFirstTouch();
    if (!ft) return current;

    return {
      ...current,
      attribution_version: ATTRIBUTION_VERSION,
      ...(ft.tp && { touchpoint_id: ft.tp }),
      first_channel: ft.ch,
      first_touch_at: ft.t,
      ...(ft.r && { first_referrer: ft.r }),
      ...(ft.p && { first_landing: ft.p }),
      ...(ft.s && { first_utm_source: ft.s }),
      ...(ft.m && { first_utm_medium: ft.m }),
      ...(ft.ca && { first_utm_campaign: ft.ca }),
      ...(ft.te && { first_utm_term: ft.te }),
      ...(ft.co && { first_utm_content: ft.co }),
      ...(ft.cid && { first_campaign_id: ft.cid }),
      ...(ft.asid && { first_adset_id: ft.asid }),
      ...(ft.aid && { first_ad_id: ft.aid }),
      ...(ft.crid && { first_creative_id: ft.crid }),
      ...(ft.pl && { first_placement: ft.pl })
    };
  } catch {
    return {};
  }
};

// Auth0 rejects the WHOLE signup when a user_metadata value exceeds 500 chars
// (incidente 16-17/jul/2026) — Meta-ads URLs blew past it and hard-failed every
// paid signup. The Auth0 copy is best-effort (PostHog carries the full
// attribution, joinable via touchpoint_id), so we add fields in priority order
// and skip any that no longer fit.
const AUTH0_METADATA_VALUE_LIMIT = 500;

const AUTH0_ATTRIBUTION_PRIORITY = [
  'attribution_version',
  'landing_visit_id',
  'touchpoint_id',
  'campaign_id',
  'adset_id',
  'ad_id',
  'creative_id',
  'first_channel',
  'first_touch_at',
  'utm_source',
  'utm_medium',
  'utm_id',
  'placement',
  'first_landing',
  'signup_page',
  'first_referrer',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'first_utm_source',
  'first_utm_medium',
  'first_utm_campaign',
  'first_utm_term',
  'first_utm_content',
  'first_campaign_id',
  'first_adset_id',
  'first_ad_id',
  'first_creative_id',
  'first_placement'
];

export const toAuth0SafeAttribution = (full: Record<string, string>): string => {
  const out: Record<string, string> = {};
  for (const key of AUTH0_ATTRIBUTION_PRIORITY) {
    const value = full[key];
    if (!value) continue;
    if (JSON.stringify({ ...out, [key]: value }).length > AUTH0_METADATA_VALUE_LIMIT) continue;
    out[key] = value;
  }
  return JSON.stringify(out);
};

/**
 * Read (or, as a fallback for users who land directly on pro.agora.red, write) the
 * first-touch cookie and register it as PostHog super-properties. Called once, right
 * after `posthog.init`, so the props are attached to every subsequent capture —
 * including the signup event on the returning `/callback` pageload.
 */
export const captureFirstTouch = (posthog: PostHog): void => {
  if (!ENABLED || typeof window === 'undefined') return;
  try {
    const ft = getOrCreateFirstTouch();
    const landingVisitId = window.__agoraLandingProbe?.visitId;

    // Exact anonymous bridge: paid /comenzar visit -> Cyclone account ->
    // onboarding events. `register` intentionally follows the current visit
    // and persists the value across the Auth0 redirect.
    if (landingVisitId && /^[a-zA-Z0-9:_-]{1,64}$/.test(landingVisitId)) {
      posthog.register({ landing_visit_id: landingVisitId });
    }

    if (ft) {
      // register_once = write-once at the device level; the cookie is already the
      // first-touch source of truth, so this never clobbers an earlier origin.
      posthog.register_once({
        ft_channel: ft.ch,
        ...(ft.r && { ft_referrer: ft.r }),
        ...(ft.s && { ft_utm_source: ft.s }),
        ...(ft.m && { ft_utm_medium: ft.m }),
        ...(ft.ca && { ft_utm_campaign: ft.ca }),
        ...(ft.te && { ft_utm_term: ft.te }),
        ...(ft.co && { ft_utm_content: ft.co }),
        ...(ft.cid && { ft_campaign_id: ft.cid }),
        ...(ft.asid && { ft_adset_id: ft.asid }),
        ...(ft.aid && { ft_ad_id: ft.aid }),
        ...(ft.crid && { ft_creative_id: ft.crid }),
        ...(ft.pl && { ft_placement: ft.pl }),
        ...(ft.p && { ft_landing: ft.p }),
        ft_at: ft.t,
        ...(ft.tp && { touchpoint_id: ft.tp }),
        attribution_version: ft.v || ATTRIBUTION_VERSION
      });
    }
  } catch {
    // Attribution is best-effort — never break analytics init.
  }
};
